import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api, ApiError, type FieldJobsSettings } from '../../lib/api.js';
import { useAuth } from '../../lib/auth-store.js';
import { FIELD_JOBS_SETTINGS_KEY, useInvalidateFieldJobsSettings } from '../../lib/settings.js';
import { CustomerPicker } from '../customer-picker.js';
import { Button } from '../ui/button.js';
import { Feedback } from '../ui/feedback.js';
import { Input } from '../ui/input.js';

/**
 * Admin → Field-created jobs ("Enercare mode"). When on, techs get a
 * "New job" button on My Day and create the Spire work order themselves —
 * for shops whose work comes from an outside dispatcher rather than the
 * office. Off for every company until an admin turns it on.
 */
export function FieldJobsSettingsPanel() {
  const role = useAuth((s) => s.user?.role);
  const canEdit = role === 'admin';
  const invalidate = useInvalidateFieldJobsSettings();
  const query = useQuery({
    queryKey: FIELD_JOBS_SETTINGS_KEY,
    queryFn: ({ signal }) => api.settings.fieldJobs(signal),
    staleTime: 60_000,
  });
  const [form, setForm] = useState<FieldJobsSettings | null>(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (query.data) setForm(query.data);
  }, [query.data]);

  const save = useMutation<FieldJobsSettings, ApiError, FieldJobsSettings>({
    mutationFn: (body) =>
      api.settings.updateFieldJobs({
        enabled: body.enabled,
        billToCustomerNo: body.billToCustomerNo,
        typeUdfKey: body.typeUdfKey,
        requirePo: body.requirePo,
      }),
    onSuccess: async (data) => {
      setForm(data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      await invalidate();
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (form) save.mutate(form);
  };

  return (
    <section
      className="rounded-xl border bg-surface p-4"
      style={{ borderColor: 'var(--color-border-tertiary)' }}
      aria-labelledby="field-jobs-title"
    >
      <h2 id="field-jobs-title" className="font-medium text-ink">
        Field-created jobs
      </h2>
      <p className="text-xs text-ink-muted mt-0.5">
        For techs who get their work from an outside dispatcher (e.g. Enercare). When on, a tech
        can create the Spire work order from their phone — PO, reference, service address, date
        and time — and it lands on their day and on the dispatch board immediately. Parts, photos,
        notes and the signature attach on site as usual.
      </p>

      {query.isError && <Feedback variant="error" size="sm" className="mt-3">{query.error.message}</Feedback>}

      {form && (
        <form onSubmit={submit} className="mt-3 space-y-3">
          <label className="inline-flex items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              checked={form.enabled}
              disabled={!canEdit}
              onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
              className="h-4 w-4"
            />
            Techs can create jobs from the field
          </label>

          <div className="space-y-1">
            <span className="text-xs uppercase tracking-wider text-ink-subtle">Default bill-to customer</span>
            {canEdit ? (
              <CustomerPicker
                value={form.billToCustomerNo ?? ''}
                valueLabel={form.billToCustomerName ?? undefined}
                onSelect={(c) => setForm({ ...form, billToCustomerNo: c.customerNo, billToCustomerName: c.name })}
              />
            ) : (
              <div className="text-sm text-ink">
                {form.billToCustomerName ?? form.billToCustomerNo ?? <span className="text-ink-muted">— not set —</span>}
              </div>
            )}
            <span className="block text-[11px] text-ink-subtle">
              Pre-selected on every field-created job (the dispatcher's account, e.g. Enercare). The tech can still bill another customer.
            </span>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-sm space-y-1">
              <span className="text-xs uppercase tracking-wider text-ink-subtle">Job-type UDF key</span>
              <Input
                value={form.typeUdfKey}
                disabled={!canEdit}
                maxLength={30}
                onChange={(e) => setForm({ ...form, typeUdfKey: e.target.value })}
                className="font-mono"
              />
              <span className="block text-[11px] text-ink-subtle">
                Sales-order header UDF that stores the job type (must exist in Spire's UDF structure to show in desktop).
              </span>
            </label>
            <label className="inline-flex items-start gap-2 text-sm text-ink pt-5">
              <input
                type="checkbox"
                checked={form.requirePo}
                disabled={!canEdit}
                onChange={(e) => setForm({ ...form, requirePo: e.target.checked })}
                className="h-4 w-4 mt-0.5"
              />
              <span>
                Require a PO number
                <span className="block text-[11px] text-ink-subtle">Enercare issues one per job; techs can't submit without it.</span>
              </span>
            </label>
          </div>

          {save.isError && <Feedback variant="error" size="sm">{save.error.message}</Feedback>}
          {canEdit && (
            <div className="flex items-center gap-3 justify-end">
              {saved && <span className="text-xs text-emerald-700">Saved.</span>}
              <Button type="submit" size="sm" disabled={save.isPending}>
                {save.isPending ? 'Saving…' : 'Save'}
              </Button>
            </div>
          )}
        </form>
      )}
    </section>
  );
}
